import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import DataTable, { useServerTable } from '../../components/ui/DataTable';
import Badge from '../../components/ui/Badge';
import Spinner from '../../components/ui/Spinner';
import Modal from '../../components/ui/Modal';
import Input, { Select } from '../../components/ui/Input';
import { adminApi } from '../../config/axios';
import { useGlobal } from '../../contexts/GlobalContext';
import { dateFormat, formatSize, useDocumentTitle } from '../../config/utils';
import { useForm } from 'react-hook-form';
import avatar from '../../assets/img/unknown_person.jpg';
import CompanyHardware from './CompanyHardware';
import SupportContactCard from '../../components/SupportContactCard';
import EmergencyControl from '../../components/ui/EmergencyControl';
import CompanyEditDialog from './CompanyEditDialog';
import AddSubscriptionFlow from '../../components/AddSubscriptionFlow';
import { formatAmount } from '../../utils/money';

const TABS = ['Devices', 'Hardware Setup', 'Support Contact', 'Transactions', 'Logs', 'Users'];

export default function CompanyProfile() {
  const { id }         = useParams();
  const navigate       = useNavigate();
  const { showToast }  = useGlobal();
  const [info, setInfo]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]     = useState('Devices');
  const [subModal, setSubModal] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const refreshInfo = useCallback(() => {
    return adminApi.get(`/companies/${id}/show`).then(res => setInfo(res.data?.data));
  }, [id]);

  useEffect(() => {
    refreshInfo().finally(() => setLoading(false));
  }, [refreshInfo]);

  useDocumentTitle(info?.company?.name ?? 'Company Profile');

  if (loading) return <Layout><div className="flex items-center justify-center py-16"><Spinner size="lg" /></div></Layout>;
  if (!info)   return <Layout><p className="text-text-muted p-6">Company not found.</p></Layout>;

  const { company } = info;

  return (
    <Layout>
      {/* Header card */}
      <div className="card-padded flex items-center gap-5 mb-6">
        <img src={company?.logo ?? avatar} alt={company?.name}
          className="w-16 h-16 rounded-2xl object-cover border border-white/[0.08] flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-xl font-bold text-text-main">{company?.name}</h1>
            <Badge variant={company?.status === 'ACTIVE' ? 'teal' : 'danger'}>{company?.status?.toLowerCase()}</Badge>
          </div>
          <p className="text-text-muted text-sm mt-0.5">{company?.email}</p>
          <div className="flex gap-6 mt-3 text-xs text-text-muted flex-wrap">
            <span>Created <strong className="text-text-main">{dateFormat(company?.created_at)}</strong></span>
            <span>Last Login <strong className="text-text-main">{company?.last_login_at ? dateFormat(company?.last_login_at) : '—'}</strong></span>
            <span>Total Devices <strong className="text-text-main">{info.totalDevices}</strong></span>
            <span>Active <strong className="text-teal">{info.activeDevices}</strong></span>
            <span>Inactive <strong className="text-danger">{info.inactiveDevices}</strong></span>
          </div>
        </div>
        <div className="flex flex-col gap-2 flex-shrink-0">
          <button onClick={() => navigate(-1)} className="btn-secondary">← Back</button>
          {/* Opens the SAME «Update Company» dialog as the Companies list: one form, one save path. */}
          <button onClick={() => setEditOpen(true)} className="btn-secondary flex items-center justify-center gap-1.5" data-testid="edit-company">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
            <span>تعديل</span>
          </button>
          <EmergencyControl
            companyId={id}
            companyName={company?.name}
            active={!!company?.emergency_active}
            variant="lg"
            onChanged={refreshInfo}
          />
        </div>
      </div>

      {/* `!!`: the API sends emergency_active as the number 0, and `0 && <banner/>` renders a stray "0" above the tabs */}
      {!!company?.emergency_active && (
        <div className="mb-6 px-4 py-3 rounded-lg flex items-center gap-3"
             style={{ background: 'rgba(220, 38, 38, 0.15)', border: '1px solid rgba(220, 38, 38, 0.4)', color: '#fecaca' }}>
          <span className="text-xl">🚨</span>
          <div className="flex-1 text-sm">
            <strong>Emergency mode is active</strong> — all screens at {company.name} are showing the safe template.
            <div className="text-xs opacity-80 mt-0.5">{company?.emergency_message ?? 'Default safe message.'}</div>
          </div>
        </div>
      )}

      <CompanyEditDialog company={editOpen ? company : null} onClose={() => setEditOpen(false)} onSaved={refreshInfo} />

      {/* Tabs */}
      <div className="flex gap-1 bg-bg-card border border-white/[0.08] rounded-lg p-1 mb-5 w-fit">
        {TABS.map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === t ? 'bg-accent text-white' : 'text-text-muted hover:text-text-main'
            }`}
          >
            {t}
          </button>
        ))}
        {tab === 'Transactions' && (
          <button onClick={() => setSubModal(true)} className="btn-primary ml-2 py-2 text-xs">+ Add Subscription</button>
        )}
      </div>

      {tab === 'Devices'         && <CompanyDevices companyId={id} />}
      {tab === 'Hardware Setup'  && <CompanyHardware companyId={id} />}
      {tab === 'Support Contact' && <SupportContactCard companyId={id} />}
      {tab === 'Transactions'    && <CompanyTransactions companyId={id} subModal={subModal} onCloseSubModal={() => setSubModal(false)} onSubscriptionChanged={refreshInfo} />}
      {tab === 'Logs'            && <CompanyLogs companyId={id} />}
      {tab === 'Users'           && <CompanyUsers companyId={id} />}
    </Layout>
  );
}

function CompanyDevices({ companyId }) {
  const fetcher = useCallback(async ({ page, perPage }) => {
    const res = await adminApi.post(`/datatable/device/view?page=${page + 1}&per_page=${perPage}`, {
      order_by: { created_at: 'desc' }, filters: {}, company_id: companyId,
    });
    return res.data;
  }, [companyId]);
  const t = useServerTable(fetcher);

  // Fetched once on tab-open with no refresh trigger of any kind — the
  // Status column (is_online, REST-only) could show a pre-migration reading
  // indefinitely. Poll at the same 15s cadence as the other REST-only surfaces.
  useEffect(() => {
    const interval = setInterval(() => t.refresh(), 15000);
    return () => clearInterval(interval);
  }, [t.refresh]);
  const cols = [
    { key: 'serial_name', header: 'Serial' },
    { key: 'name',        header: 'Name' },
    { key: 'is_online',   header: 'Status', render: r => <Badge variant={r.is_online ? 'teal' : 'danger'} dot>{r.is_online ? 'Online' : 'Offline'}</Badge> },
    { key: 'status',      header: 'Subscription', render: r => <Badge variant={r.status === 'ACTIVE' ? 'teal' : r.status === 'EXPIRED' ? 'danger' : 'gold'}>{r.status?.toLowerCase()}</Badge> },
    { key: 'created_at',  header: 'Added', render: r => dateFormat(r.created_at) },
  ];
  return <div className="card"><DataTable columns={cols} {...t} onPageChange={t.setPage} onPerPageChange={t.setPerPage} /></div>;
}

function CompanyTransactions({ companyId, subModal, onCloseSubModal, onSubscriptionChanged }) {
  const { showToast }  = useGlobal();
  const fetcher = useCallback(async ({ page, perPage }) => {
    const res = await adminApi.post(`/datatable/subscriptions?page=${page + 1}&per_page=${perPage}`, {
      order_by: { created_at: 'desc' }, filters: {}, company_id: companyId,
    });
    return res.data;
  }, [companyId]);
  const t = useServerTable(fetcher);
  const cols = [
    { key: 'subscription_id', header: 'Transaction ID' },
    { key: 'title',    header: 'Package' },
    { key: 'paid_price', header: 'Amount', render: r => r.paid_price === 0 ? 'Free' : formatAmount(r.paid_price, null) },
    { key: 'currency', header: 'Currency', render: r => (r.paid_price === 0 ? '—' : (r.currency || '—')) },
    { key: 'period',   header: 'Period', render: r => r.is_free ? '7 days' : r.period?.toLowerCase().replace(/_/g, ' ') },
    { key: 'note',     header: 'Payment Method' },
    { key: 'type',     header: 'Type', render: r => r.type?.toLowerCase() },
    { key: 'status',   header: 'Status', render: r => <Badge variant={r.status === 'ACTIVE' ? 'teal' : 'danger'}>{r.status?.toLowerCase()}</Badge> },
    { key: 'start_date', header: 'Start', render: r => dateFormat(r.start_date) },
    { key: 'end_date',   header: 'End',   render: r => dateFormat(r.end_date) },
  ];

  return (
    <>
      <div className="card"><DataTable columns={cols} {...t} onPageChange={t.setPage} onPerPageChange={t.setPerPage} /></div>
      <AddSubscriptionFlow companyId={companyId} open={subModal} onClose={onCloseSubModal} onDone={() => { t.refresh(); onSubscriptionChanged?.(); }} />
    </>
  );
}

function CompanyLogs({ companyId }) {
  const { showToast } = useGlobal();
  const [logDetail, setLogDetail] = useState(null);
  const fetcher = useCallback(async ({ page, perPage }) => {
    const res = await adminApi.post(`/datatable/logs?page=${page + 1}&per_page=${perPage}`, {
      order_by: { created_at: 'desc' }, filters: {}, company_id: companyId,
    });
    return res.data;
  }, [companyId]);
  const t = useServerTable(fetcher);
  const cols = [
    { key: 'user_name', header: 'User' },
    { key: 'action',    header: 'Action', render: r => <code className="text-xs bg-white/[0.06] px-1.5 py-0.5 rounded">{r.action}</code> },
    { key: 'created_at', header: 'Date', render: r => dateFormat(r.created_at) },
    { key: 'ip',        header: 'IP' },
  ];
  return (
    <>
      <div className="card">
        <DataTable columns={cols} {...t} onPageChange={t.setPage} onPerPageChange={t.setPerPage}
          actions={row => (
            <button className="btn-icon" onClick={() => setLogDetail(row)}>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </button>
          )}
        />
      </div>
      <Modal open={!!logDetail} onClose={() => setLogDetail(null)} title="Log Detail">
        <pre className="text-xs text-text-muted whitespace-pre-wrap">{JSON.stringify(logDetail, null, 2)}</pre>
      </Modal>
    </>
  );
}

function CompanyUsers({ companyId }) {
  const fetcher = useCallback(async ({ page, perPage }) => {
    const res = await adminApi.post(`/datatable/users/view?page=${page + 1}&per_page=${perPage}`, {
      order_by: { created_at: 'desc' }, filters: {}, company_id: companyId,
    });
    return res.data;
  }, [companyId]);
  const t = useServerTable(fetcher);
  const cols = [
    { key: 'name',       header: 'Name', render: r => (
      <div className="flex items-center gap-2">
        <img src={r.image ?? avatar} alt={r.name} className="w-7 h-7 rounded-full object-cover" />
        <span>{r.name}</span>
      </div>
    )},
    { key: 'email',      header: 'Email' },
    { key: 'phone',      header: 'Phone' },
    { key: 'status',     header: 'Status', render: r => <Badge variant="teal">{r.status?.toLowerCase()}</Badge> },
    { key: 'created_at', header: 'Joined', render: r => dateFormat(r.created_at) },
  ];
  return <div className="card"><DataTable columns={cols} {...t} onPageChange={t.setPage} onPerPageChange={t.setPerPage} /></div>;
}
